import { NgClass } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { Store } from '@ngrx/store';
import { ImageCdnSrcDirective } from '@polaris-customerweb/sharedcomponents.shared';
import { takeWhile, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/models/app-state';
import {
    SetIsShowingDesignatedPavedTrails,
    SetIsShowingDesignatedUnpavedTrails,
    SetIsShowingUnmaintainedTrails,
    SetShouldShowTrailDifficulty,
    SetTrailFilter,
} from 'src/app/state/map/layer-visibility/layer-visibility.actions';
import {
    selectIsShowingDesignatedPavedTrails,
    selectIsShowingDesignatedUnpavedTrails,
    selectIsShowingSatellite,
    selectIsShowingTrailDifficulty,
    selectIsShowingUnmaintainedTrails,
    selectTrailFilter,
} from 'src/app/state/map/layer-visibility/layer-visibility.reducer';

@Component({
    imports: [ImageCdnSrcDirective, MatCheckboxModule, MatRadioModule, NgClass],
    selector: 'rdx-offroad-trail-list',
    standalone: true,
    styleUrls: ['./offroad-trail-list.component.scss'],
    templateUrl: './offroad-trail-list.component.html',
})
export class OffroadTrailListComponent implements OnInit {
    @Input() isShowingSatellite = false;

    isAllTrailsShowing = false;
    isShowingDesignatedUnpavedTrails = true;
    isShowingDesignatedPavedTrails = true;
    isShowingUnmaintainedTrails = true;
    isShowingOhmTrails = false;
    isOhmDisabled = false;
    isShowingAtvTrails = false;
    isAtvDisabled = false;
    isShowingRov50Trails = false;
    isRov50Disabled = false;
    isShowingRovTrails = false;
    isRovDisabled = false;
    isShowingOtherTrails = false;
    isShowingTrailDifficulty = false;
    isOtherDisabled = false;
    isGrayImageOhm = false;
    isGrayImageAtv = false;
    isGrayImageRov50 = false;
    isGrayImageRov = false;

    ohmImg: string;
    atvImg: string;
    rov50Img: string;
    rovImg: string;
    otherImg: string;

    private ohmNonSatImg =
        '/assets/icons/map-legend-icons/orv/icon-offroadtrails-ohm.svg';
    private ohmImgSat =
        '/assets/icons/map-legend-icons/orv/icon-offroadtrails-ohm-sat.svg';

    private atvNonSatImg =
        '/assets/icons/map-legend-icons/orv/icon-offroadtrails-atv.svg';
    private atvImgSat =
        '/assets/icons/map-legend-icons/orv/icon-offroadtrails-atv-sat.svg';

    private rov50NonSatImg =
        '/assets/icons/map-legend-icons/orv/icon-offroadtrails-rov50.svg';
    private rov50ImgSat =
        '/assets/icons/map-legend-icons/orv/icon-offroadtrails-rov50-sat.svg';

    private rovNonSatImg =
        '/assets/icons/map-legend-icons/orv/icon-offroadtrails-rov.svg';
    private rovImgSat =
        '/assets/icons/map-legend-icons/orv/icon-offroadtrails-rov-sat.svg';

    private otherNonSatImg =
        '/assets/icons/map-legend-icons/orv/icon-offroadtrails-other.svg';
    private otherImgSat =
        '/assets/icons/map-legend-icons/orv/icon-offroadtrails-other-sat.svg';

    private isDestroyed = false;

    constructor(private store: Store<AppState>) {}

    ngOnInit(): void {
        this.addLayerVisibilityListeners();
    }

    toggleAllOffroadTrails(): void {
        this.store.dispatch(new SetTrailFilter(undefined));
    }

    setIsShowingDesignatedUnpaved(isShowing: boolean) {
        this.store.dispatch(new SetIsShowingDesignatedUnpavedTrails(isShowing));
    }

    setIsShowingDesignatedPaved(isShowing: boolean) {
        this.store.dispatch(new SetIsShowingDesignatedPavedTrails(isShowing));
    }

    setIsShowingUnmaintained(isShowing: boolean) {
        this.store.dispatch(new SetIsShowingUnmaintainedTrails(isShowing));
    }

    setIsShowingOhm() {
        this.store.dispatch(new SetTrailFilter('OHM'));
    }

    setIsShowingAtv() {
        this.store.dispatch(new SetTrailFilter('ATV'));
    }

    setIsShowingRov50() {
        this.store.dispatch(new SetTrailFilter('ROV50'));
    }

    setIsShowingRov() {
        this.store.dispatch(new SetTrailFilter('ROV'));
    }

    setIsShowingOther() {
        this.store.dispatch(new SetTrailFilter('HLV'));
    }

    setTrailDifficulty(isShowing: boolean) {
        this.store.dispatch(new SetShouldShowTrailDifficulty(isShowing));
    }

    private addLayerVisibilityListeners() {
        this.store
            .select(selectTrailFilter)
            .pipe(
                takeWhile(() => !this.isDestroyed),
                withLatestFrom(this.store.select(selectIsShowingSatellite))
            )
            .subscribe(([type, isSatellite]) => {
                this.isAllTrailsShowing = false;
                this.isShowingOhmTrails = false;
                this.isShowingAtvTrails = false;
                this.isShowingRov50Trails = false;
                this.isShowingRovTrails = false;
                this.isShowingOtherTrails = false;
                this.isOhmDisabled = false;
                this.isAtvDisabled = false;
                this.isRov50Disabled = false;
                this.isRovDisabled = false;
                this.isOtherDisabled = false;
                this.isGrayImageOhm = false;
                this.isGrayImageAtv = false;
                this.isGrayImageRov50 = false;
                this.isGrayImageRov = false;
                switch (type) {
                    case undefined:
                        if (isSatellite) {
                            this.ohmImg = this.ohmImgSat;
                            this.atvImg = this.atvImgSat;
                            this.rov50Img = this.rov50ImgSat;
                            this.rovImg = this.rovImgSat;
                            this.otherImg = this.otherImgSat;
                        } else {
                            this.ohmImg = this.ohmNonSatImg;
                            this.atvImg = this.atvNonSatImg;
                            this.rov50Img = this.rov50NonSatImg;
                            this.rovImg = this.rovNonSatImg;
                            this.otherImg = this.otherNonSatImg;
                        }
                        this.isAllTrailsShowing = true;
                        this.isOhmDisabled = true;
                        this.isAtvDisabled = true;
                        this.isRov50Disabled = true;
                        this.isRovDisabled = true;
                        this.isOtherDisabled = true;
                        break;
                    case 'OHM':
                        if (isSatellite) {
                            this.ohmImg = this.ohmImgSat;
                            this.atvImg = this.atvImgSat;
                            this.rov50Img = this.rov50ImgSat;
                            this.rovImg = this.rovImgSat;
                            this.otherImg = this.otherImgSat;
                        } else {
                            this.ohmImg = this.ohmNonSatImg;
                            this.atvImg = this.atvNonSatImg;
                            this.rov50Img = this.rov50NonSatImg;
                            this.rovImg = this.rovNonSatImg;
                            this.otherImg = this.otherNonSatImg;
                        }
                        this.isShowingOhmTrails = true;
                        this.isAtvDisabled = true;
                        this.isRov50Disabled = true;
                        this.isRovDisabled = true;
                        this.isOtherDisabled = true;
                        break;
                    case 'ATV':
                        if (isSatellite) {
                            this.ohmImg = this.ohmImgSat;
                            this.atvImg = this.atvImgSat;
                            this.rov50Img = this.rov50ImgSat;
                            this.rovImg = this.rovImgSat;
                            this.otherImg = this.otherImgSat;
                        } else {
                            this.ohmImg = this.ohmNonSatImg;
                            this.atvImg = this.atvNonSatImg;
                            this.rov50Img = this.rov50NonSatImg;
                            this.rovImg = this.rovNonSatImg;
                            this.otherImg = this.otherNonSatImg;
                        }
                        this.isGrayImageOhm = true;
                        this.isGrayImageAtv = false;
                        this.isGrayImageRov50 = false;
                        this.isGrayImageRov = false;
                        this.isShowingAtvTrails = true;
                        this.isRov50Disabled = true;
                        this.isRovDisabled = true;
                        this.isOtherDisabled = true;
                        break;
                    case 'ROV50':
                        if (isSatellite) {
                            this.ohmImg = this.ohmImgSat;
                            this.atvImg = this.atvImgSat;
                            this.rov50Img = this.rov50ImgSat;
                            this.rovImg = this.rovImgSat;
                            this.otherImg = this.otherImgSat;
                        } else {
                            this.ohmImg = this.ohmNonSatImg;
                            this.atvImg = this.atvNonSatImg;
                            this.rov50Img = this.rov50NonSatImg;
                            this.rovImg = this.rovNonSatImg;
                            this.otherImg = this.otherNonSatImg;
                        }
                        this.isGrayImageOhm = true;
                        this.isGrayImageAtv = true;
                        this.isGrayImageRov50 = false;
                        this.isGrayImageRov = false;
                        this.isShowingRov50Trails = true;
                        this.isRovDisabled = true;
                        this.isOtherDisabled = true;
                        break;
                    case 'ROV':
                        if (isSatellite) {
                            this.ohmImg = this.ohmImgSat;
                            this.atvImg = this.atvImgSat;
                            this.rov50Img = this.rov50ImgSat;
                            this.rovImg = this.rovImgSat;
                            this.otherImg = this.otherImgSat;
                        } else {
                            this.ohmImg = this.ohmNonSatImg;
                            this.atvImg = this.atvNonSatImg;
                            this.rov50Img = this.rov50NonSatImg;
                            this.rovImg = this.rovNonSatImg;
                            this.otherImg = this.otherNonSatImg;
                        }
                        this.isGrayImageOhm = true;
                        this.isGrayImageAtv = true;
                        this.isGrayImageRov50 = true;
                        this.isGrayImageRov = false;
                        this.isShowingRovTrails = true;
                        this.isOtherDisabled = true;
                        break;
                    case 'HLV':
                        if (isSatellite) {
                            this.ohmImg = this.ohmImgSat;
                            this.atvImg = this.atvImgSat;
                            this.rov50Img = this.rov50ImgSat;
                            this.rovImg = this.rovImgSat;
                            this.otherImg = this.otherImgSat;
                        } else {
                            this.ohmImg = this.ohmNonSatImg;
                            this.atvImg = this.atvNonSatImg;
                            this.rov50Img = this.rov50NonSatImg;
                            this.rovImg = this.rovNonSatImg;
                            this.otherImg = this.otherNonSatImg;
                        }
                        this.isGrayImageOhm = true;
                        this.isGrayImageAtv = true;
                        this.isGrayImageRov50 = true;
                        this.isGrayImageRov = true;
                        this.isShowingOtherTrails = true;
                        break;
                    default:
                        break;
                }
            });

        this.store
            .select(selectIsShowingDesignatedUnpavedTrails)
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(
                (isShowingDesignatedUnpavedTrails) =>
                    (this.isShowingDesignatedUnpavedTrails =
                        isShowingDesignatedUnpavedTrails)
            );

        this.store
            .select(selectIsShowingDesignatedPavedTrails)
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(
                (isShowingDesignatedPavedTrails) =>
                    (this.isShowingDesignatedPavedTrails =
                        isShowingDesignatedPavedTrails)
            );

        this.store
            .select(selectIsShowingUnmaintainedTrails)
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(
                (isShowingUnmmaintainedTrails) =>
                    (this.isShowingUnmaintainedTrails = Boolean(
                        isShowingUnmmaintainedTrails
                    ))
            );

        this.store
            .select(selectIsShowingTrailDifficulty)
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(
                (show) => (this.isShowingTrailDifficulty = Boolean(show))
            );
    }
}
